import { Trans } from 'react-i18next';

import { loaders, SampleProjectStatusData } from 'entities';

import { Column, Button, Alert, Skeleton, Row } from 'components/Blocks';

import Stepper from 'components/Stepper';
import { EarthIcon, FolderSearchIcon, TargetIcon } from 'components/Icons';

import { TargetForm } from './TargetForm';
import { useRequestRecruitmentModal } from './requestRecruitment.context';

import { useLang } from 'hooks';

import { COUNTRIES } from 'utils/countries';
import { cascadeEval } from 'utils/conditions';
import { listGet } from 'utils/array';

function isSampleIdValid(
  sampleId: string,
  projectCountries: string[],
  sampleStatuses?: SampleProjectStatusData[],
): boolean {
  return projectCountries.some((country) => {
    const config = listGet(sampleStatuses || [], sampleId);

    return config?.countries[country]?.status === 'Live';
  });
}

export function SampleForm() {
  const { t } = useLang();

  const { form, project, task, sampleIdOnQuery } = useRequestRecruitmentModal();

  const sampleIdStatuses = loaders.projects.getSampleProjectsStatuses.use({
    params: {
      projectId: project.id,
      taskId: task.id,
    },

    onDataChange: (statusMap) => {
      if (sampleIdOnQuery && isSampleIdValid(sampleIdOnQuery, project.countries, statusMap)) {
        form.dispatch({ sampleId: sampleIdOnQuery });
      }
    },
  });

  const sampleCountries = new Set(
    sampleIdStatuses.data?.map(({ countries }) => Object.keys(countries)).flat(),
  );

  const possibleCountries = COUNTRIES.filter(
    ({ sampleId, value, externalPanel }) =>
      !externalPanel && sampleId && sampleCountries.has(value),
  );

  const taskSampleIds = task?.sampleProjectIds ?? [];

  const availableIds = taskSampleIds.filter((sampleId) =>
    isSampleIdValid(sampleId, project.countries, sampleIdStatuses.data),
  );

  return (
    <Column>
      <Stepper
        stepClassName="w-20 rounded-lg p-2 transition-colors flex-col"
        activeStepClassName="bg-primary text-primary-foreground"
        steps={[
          {
            content: (
              <>
                <FolderSearchIcon size={20} />

                <span className="text-sm">{form.state.sampleId || 'Sample'}</span>
              </>
            ),
            activeContent: ({ next }) => (
              <>
                {cascadeEval([
                  {
                    is: !sampleIdStatuses.data,
                    then: () => <Skeleton width="100%" height={45} quantity={2} />,
                  },

                  {
                    is: availableIds.length,
                    then: () => (
                      <Column alignItems="center">
                        <p className="text-center text-lg font-semibold">
                          {t('chose-project-to-recruit')}
                        </p>

                        <Row flexWrap="wrap">
                          {availableIds.map((sampleId) => (
                            <Button
                              key={sampleId}
                              className="w-fit"
                              variant={form.state.sampleId === sampleId ? 'filled' : 'outlined'}
                              onClick={() => {
                                const currentCountryValid =
                                  listGet(sampleIdStatuses.data || [], sampleId)?.countries?.[
                                    form.state.country
                                  ]?.status === 'Live';

                                const country = currentCountryValid ? form.state.country : '';

                                form.dispatch({ sampleId, country });
                                next();
                              }}
                            >
                              {sampleId}
                            </Button>
                          ))}
                        </Row>
                      </Column>
                    ),
                  },

                  {
                    is: true,
                    then: () => (
                      <Alert title={t('only-live-projects-can-recruit')} level="info">
                        <Trans i18nKey="recruit-sample-explanation"></Trans>
                      </Alert>
                    ),
                  },
                ])}
              </>
            ),
          },
          {
            disabled: !form.state.sampleId,

            content: (
              <>
                <EarthIcon size={20} color="currentColor" />

                <span className="text-sm">{form.state.country || t('country')}</span>
              </>
            ),

            activeContent: ({ next }) => (
              <Column alignItems="center">
                <p className="text-center text-lg font-semibold">{t('chose-country-to-recruit')}</p>

                <Row flexWrap="wrap">
                  {possibleCountries.map((country) => (
                    <Button
                      key={country.value}
                      className="fit-w"
                      variant={form.state.country === country.value ? 'filled' : 'outlined'}
                      onClick={() => {
                        form.dispatch({ country: country.value });
                        next();
                      }}
                    >
                      {country.label}
                    </Button>
                  ))}
                </Row>
              </Column>
            ),
          },
          {
            disabled: !form.state.country,

            activeContent: <TargetForm />,

            content: (
              <>
                <TargetIcon size={20} color="currentColor" />

                <span className="text-sm">Data</span>
              </>
            ),
          },
        ]}
      />
    </Column>
  );
}
